import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Platform } from 'react-native';
import { auth, db, isFirebaseConfigured } from '@/utils/firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  User as FirebaseUser,
  applyActionCode,
  deleteUser,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { User } from '@/types/app';
import { Alert } from 'react-native';
import { logger } from '@/utils/logger';


// Interface do Contexto com função isPremium
interface AuthContextType {
  user: User | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (nome: string, email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (oobCode: string, newPassword: string) => Promise<void>;
  verifyResetCode: (oobCode: string) => Promise<string>;
  verifyEmailCode: (oobCode: string) => Promise<void>;
  loading: boolean;
  isPremium: () => boolean;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isFirebaseConfigured || !auth || !db) {
      setUser(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        if (!firebaseUser.emailVerified) {
          setUser(null);
          setLoading(false);
          return;
        }
        // Primeiro, define um usuário otimista para evitar flash de loading
        const optimisticUser: User = {
          id: firebaseUser.uid,
          nome: firebaseUser.displayName || firebaseUser.email?.split('@')?.[0] || 'Usuário',
          email: firebaseUser.email || '',
          fotoURL: firebaseUser.photoURL || undefined,
          emailVerificado: firebaseUser.emailVerified,
          plano: 'free',
        };
        setUser(optimisticUser);

        try {
          const userDocRef = doc(db, 'usuarios', firebaseUser.uid);
          const docSnap = await getDoc(userDocRef);

          if (docSnap.exists()) {
            setUser({
              ...optimisticUser,
              ...docSnap.data(),
            } as User);
          } else {
            // Criar documento se não existir (caso de login Google em novo usuário)
            const userData = {
              nome: firebaseUser.displayName || firebaseUser.email?.split('@')?.[0] || 'Usuário',
              email: firebaseUser.email,
              fotoURL: firebaseUser.photoURL || null,
              plano: 'free',
            };
            await setDoc(userDocRef, userData);
            setUser({
              ...optimisticUser,
              ...userData,
            } as User);
          }
        } catch (error) {
          logger.authError('Error fetching user data', error);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!auth) {
      throw new Error('Firebase não configurado neste build.');
    }
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Bloquear usuário não verificado
      if (!userCredential.user.emailVerified) {
        try {
          await sendEmailVerification(userCredential.user, {
            url: __DEV__ ? 'http://localhost:8081/login' : 'https://poenoracha.com.br/login',
            handleCodeInApp: false,
          });
        } catch (error) {
          logger.authError('Error sending verification email on sign in', error);
        }
        // Fazer logout imediato para não deixar sessão ativa
        await firebaseSignOut(auth);
        const error = new Error('E-mail não verificado. Enviamos um novo e-mail de verificação.');
        (error as any).code = 'auth/email-not-verified';
        throw error;
      }
    } catch (error: any) {
      logger.authError('Sign in error', error);
      throw error;
    }
  };

  const signUp = async (nome: string, email: string, password: string) => {
    if (!auth || !db) {
      throw new Error('Firebase não configurado neste build.');
    }
    let firebaseUser: FirebaseUser | null = null;
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      firebaseUser = userCredential.user;

      const userData: Omit<User, 'id' | 'emailVerificado'> = { nome, email, plano: 'free' };

      await setDoc(doc(db, 'usuarios', firebaseUser.uid), userData);
      await sendEmailVerification(firebaseUser, {
        url: __DEV__ ? 'http://localhost:8081/login' : 'https://poenoracha.com.br/login',
        handleCodeInApp: false,
      });

      // Logout forçado - usuário deve verificar email antes de usar o app
      await firebaseSignOut(auth);
    } catch (error: any) {
      if (firebaseUser) {
        try {
          await deleteUser(firebaseUser);
        } catch (deleteError) {
          logger.authError('Error deleting user after failed signup flow', deleteError);
        }
        try {
          await firebaseSignOut(auth);
        } catch (signOutError) {
          logger.authError('Error signing out after failed signup flow', signOutError);
        }
      }
      logger.authError('Sign up error', error);
      throw error;
    }
  };

  const signOut = async () => {
    if (!auth) return;
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      logger.authError('Sign out error', error);
    }
  };

  // Login com Google - VERSÃO SIMPLES COM LOGS DE DIAGNÓSTICO
  const signInWithGoogle = async () => {
    console.log('[AUTH DEBUG] signInWithGoogle: Iniciando...');
    try {
      if (!auth || !db) {
        throw new Error('Firebase não configurado neste build.');
      }
      if (Platform.OS === 'web') {
        console.log('[AUTH DEBUG] Platform: web');

        const provider = new GoogleAuthProvider();
        provider.addScope('email');
        provider.addScope('profile');
        console.log('[AUTH DEBUG] GoogleAuthProvider criado com scopes');

        console.log('[AUTH DEBUG] Chamando signInWithPopup...');
        const result = await signInWithPopup(auth, provider);
        console.log('[AUTH DEBUG] signInWithPopup retornou resultado:', result ? 'OK' : 'NULL');

        const firebaseUser = result.user;
        console.log('[AUTH DEBUG] Firebase user UID:', firebaseUser?.uid);

        // Verificar se o usuário já existe no Firestore
        const userDocRef = doc(db, 'usuarios', firebaseUser.uid);
        console.log('[AUTH DEBUG] Buscando usuário no Firestore...');
        const docSnap = await getDoc(userDocRef);
        console.log('[AUTH DEBUG] Doc exists:', docSnap.exists());

        if (!docSnap.exists()) {
          const userData = {
            nome: firebaseUser.displayName || 'Usuário Google',
            email: firebaseUser.email,
            fotoURL: firebaseUser.photoURL || null,
          };

          console.log('[AUTH DEBUG] Criando novo usuário no Firestore...');
          await setDoc(userDocRef, userData);
          console.log('[AUTH DEBUG] Usuário criado com sucesso');
          logger.info('New Google user created in Firestore');
        }

        console.log('[AUTH DEBUG] Login Google concluído com sucesso!');
        logger.info('Google sign-in successful');
      } else {
        console.log('[AUTH DEBUG] Platform não é web:', Platform.OS);
        throw new Error('Login com Google não disponível nesta plataforma. Por favor, use email e senha.');
      }
    } catch (error: any) {
      console.error('[AUTH DEBUG] ERRO no signInWithGoogle:', {
        code: error?.code,
        message: error?.message,
        name: error?.name,
        stack: error?.stack?.substring(0, 500)
      });
      logger.authError('Google sign-in error', error);

      if (error.code === 'auth/popup-closed-by-user') {
        console.log('[AUTH DEBUG] Popup fechado pelo usuário - ignorando');
        return; // Usuário fechou o popup, não é erro crítico
      }
      if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup bloqueado pelo navegador. Por favor, permita popups para este site.');
      }
      if (error.code === 'auth/account-exists-with-different-credential') {
        throw new Error('Já existe uma conta com este email. Tente fazer login com email e senha.');
      }
      if (error.code === 'auth/network-request-failed') {
        throw new Error('Erro de conexão. Verifique sua internet, firewall ou desative bloqueadores de anúncios (AdBlock) que possam estar impedindo o login.');
      }

      throw error;
    }
  };

  const updateProfile = async (updates: Partial<User>) => {
    if (!user) return;

    try {
      const allowedKeys = new Set(['nome', 'fotoURL', 'chavePix']);
      const updatesLimpos: Record<string, any> = {};

      Object.keys(updates).forEach(key => {
        if (!allowedKeys.has(key)) return;
        const value = (updates as any)[key];
        if (value !== undefined) {
          updatesLimpos[key] = value;
        }
      });

      if (Object.keys(updatesLimpos).length === 0) return;

      const userDocRef = doc(db, 'usuarios', user.id);
      await updateDoc(userDocRef, updatesLimpos);
      setUser(currentUser => currentUser ? { ...currentUser, ...updatesLimpos } : null);
    } catch (error) {
      logger.error('Error updating profile', { error });
      throw error;
    }
  };

  const resendVerificationEmail = async () => {
    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      try {
        await sendEmailVerification(firebaseUser, {
          url: __DEV__ ? 'http://localhost:8081/login' : 'https://poenoracha.com.br/login',
          handleCodeInApp: false,
        });
      } catch (error) {
        logger.authError('Error resending verification email', error);
        throw error;
      }
    }
  };

  const verifyEmailCode = async (oobCode: string) => {
    if (!auth) {
      throw new Error('Firebase não configurado neste build.');
    }
    try {
      await applyActionCode(auth, oobCode);
    } catch (error: any) {
      logger.authError('Error applying email verification code', error);
      if (error.code === 'auth/invalid-action-code') {
        throw new Error('Link inválido ou expirado. Solicite um novo.')
      }
      if (error.code === 'auth/expired-action-code') {
        throw new Error('Link expirado. Solicite um novo.')
      }
      throw error;
    }
  }

  // Esqueci minha senha - envia email de recuperação
  const forgotPassword = async (email: string) => {
    try {
      // Configurar URL de redirecionamento para nossa página customizada
      const actionCodeSettings = {
        // URL para onde o usuário será redirecionado após clicar no link
        url: __DEV__
          ? 'http://localhost:8081/update-password'
          : 'https://poenoracha.com.br/update-password',
        // Permitir que o link seja aberto no app mobile
        handleCodeInApp: true,
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      logger.info('Password reset email sent');
    } catch (error: any) {
      logger.authError('Error sending password reset email', error);

      // Traduzir erros do Firebase
      if (error.code === 'auth/user-not-found') {
        throw new Error('Nenhuma conta encontrada com este e-mail.');
      }
      if (error.code === 'auth/invalid-email') {
        throw new Error('E-mail inválido.');
      }
      if (error.code === 'auth/too-many-requests') {
        throw new Error('Muitas tentativas. Aguarde alguns minutos.');
      }
      throw error;
    }
  };

  // Verificar código de redefinição (oobCode da URL)
  const verifyResetCode = async (oobCode: string): Promise<string> => {
    try {
      const email = await verifyPasswordResetCode(auth, oobCode);
      return email;
    } catch (error: any) {
      logger.authError('Error verifying reset code', error);

      if (error.code === 'auth/invalid-action-code') {
        throw new Error('Link inválido ou expirado. Solicite um novo.');
      }
      if (error.code === 'auth/expired-action-code') {
        throw new Error('Link expirado. Solicite um novo.');
      }
      throw error;
    }
  };

  // Redefinir senha com código oobCode
  const resetPassword = async (oobCode: string, newPassword: string) => {
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      logger.info('Password reset successful');
    } catch (error: any) {
      logger.authError('Error resetting password', error);

      if (error.code === 'auth/invalid-action-code') {
        throw new Error('Link inválido ou expirado. Solicite um novo.');
      }
      if (error.code === 'auth/expired-action-code') {
        throw new Error('Link expirado. Solicite um novo.');
      }
      if (error.code === 'auth/weak-password') {
        throw new Error('Senha muito fraca. Use pelo menos 6 caracteres.');
      }
      throw error;
    }
  };

  const isPremium = (): boolean => {
    logger.debug('Checking premium status');

    if (!user || !user.plano) {
      logger.debug('User without plan');
      return false;
    }

    logger.debug('User plan', { plano: user.plano });

    if (user.plano === 'vitalicio' || user.plano === 'anual') {
      return true;
    }

    if (user.current_period_end) {
      try {
        const agora = new Date();
        let dataExpiracao: Date;
        const cpe: any = user.current_period_end;
        if (typeof cpe.toDate === 'function') {
          dataExpiracao = cpe.toDate();
        } else if (typeof cpe.toMillis === 'function') {
          dataExpiracao = new Date(cpe.toMillis());
        } else {
          dataExpiracao = new Date(cpe);
        }
        const isActive = agora < dataExpiracao;
        return isActive;
      } catch (error) {
        logger.error('Error checking current_period_end', { error });
      }
    }

    if (user.assinaturaExpiraEm) {
      try {
        const agora = new Date();
        let dataExpiracao: Date;

        const expiraEm = user.assinaturaExpiraEm as any;
        if (typeof expiraEm.toDate === 'function') {
          dataExpiracao = expiraEm.toDate();
        } else if (typeof expiraEm.toMillis === 'function') {
          dataExpiracao = new Date(expiraEm.toMillis());
        } else {
          dataExpiracao = new Date(expiraEm);
        }

        const isActive = agora < dataExpiracao;
        return isActive;
      } catch (error) {
        logger.error('Error checking expiration date', { error });
        return false;
      }
    }

    logger.debug('Premium conditions not met');
    return false;
  };

  const refreshUserProfile = async () => {
    if (!auth.currentUser) return;

    try {
      const userDocRef = doc(db, 'usuarios', auth.currentUser.uid);
      const docSnap = await getDoc(userDocRef);

      if (docSnap.exists()) {
        setUser({
          id: auth.currentUser.uid,
          email: auth.currentUser.email!,
          emailVerificado: auth.currentUser.emailVerified,
          ...docSnap.data(),
        } as User);
        logger.info('User profile refreshed manually');
      }
    } catch (error) {
      logger.error('Error refreshing user profile', { error });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signInWithGoogle,
        signUp,
        signOut,
        updateProfile,
        resendVerificationEmail,
        forgotPassword,
        resetPassword,
        verifyResetCode,
        verifyEmailCode,
        loading,
        isPremium,
        refreshUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
